import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { ChevronLeftIcon, EyeCloseIcon, EyeIcon, MetaIcon } from "../../icons";
import Label from "../form/Label";
import Input from "../form/input/InputField";
import Checkbox from "../form/input/Checkbox";
import { useAuth } from "../../context/AuthContext";
import { useSights } from "../../context/SightContext";
import { useWidgets } from "../../context/WidgetContext";
import axios from "../../lib/axios";
import useGeoLocation from "react-ipgeolocation";
import { enablePushNotifications } from "../../services/firebasePushNotification";
import PhoneInput, {
  type Country,
  getCountryCallingCode,
} from "react-phone-number-input";
import "react-phone-number-input/style.css";
import PhoneCountrySelectDrawer from "./PhoneCountrySelectDrawer";
import { parsePhoneNumberFromString } from "libphonenumber-js";

// Max national digit lengths per country (ISO 3166-1 alpha-2 codes)
export const PHONE_MAX_LENGTHS: Partial<Record<string, number>> = {
  // South Asia
  IN: 10, PK: 10, BD: 10, LK: 9, NP: 10, AF: 9, MV: 7,
  // East Asia
  CN: 11, JP: 10, KR: 10, TW: 9, HK: 8, MO: 8,
  // Southeast Asia
  ID: 12, PH: 10, VN: 9, TH: 9, MY: 9, SG: 8, MM: 9, KH: 9, LA: 9, BN: 7,
  // Middle East
  AE: 9, SA: 9, QA: 8, KW: 8, BH: 8, OM: 8, JO: 9, IL: 9, TR: 10, IR: 10,
  IQ: 10, YE: 9, SY: 9, LB: 8,
  // Africa
  NG: 10, ZA: 9, EG: 10, KE: 9, GH: 9, ET: 9, TZ: 9, UG: 9, TN: 8, MA: 9,
  DZ: 9, SD: 9, MZ: 9, AO: 9, CM: 9, CI: 8, SN: 9, ZW: 9,
  // Europe
  GB: 10, DE: 10, FR: 9, IT: 10, ES: 9, NL: 9, BE: 9, SE: 9, NO: 8, DK: 8,
  FI: 9, PL: 9, RU: 10, UA: 9, PT: 9, GR: 10, AT: 10, CH: 9, CZ: 9, HU: 9,
  RO: 9, BG: 9, HR: 9, SK: 9, LT: 8, LV: 8, EE: 8,
  // Americas
  US: 10, CA: 10, MX: 10, BR: 11, AR: 10, CO: 10, PE: 9, CL: 9, VE: 10,
  EC: 9, BO: 8, PY: 9, UY: 8,
  // Oceania
  AU: 9, NZ: 8,
};

export default function SignUpForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState<string | undefined>(undefined);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [installUrl, setInstallUrl] = useState<string | null>(null);
  const [pendingShop, setPendingShop] = useState("");
  const { register, loginWithGoogle, loginWithFacebook, currentShop } =
    useAuth();
  const { loadSights, createSight, sights } = useSights();
  const { availableWidgets } = useWidgets();
  const [searchParams] = useSearchParams();
  const { country, error: geoError, isLoading: geoLoading } = useGeoLocation();

  async function handleGoogleLogin() {
    setError("");
    setSocialLoading("google");
    try {
      const result = await loginWithGoogle();
      if (result.success) {
        await handlePostRegistrationRedirect();
      } else {
        // Handle existing account error specifically
        if (result.error?.includes("Account already exists")) {
          setError(result.error);
        } else {
          setError(result.error || "Google sign up failed. Please try again.");
        }
      }
    } catch {
      setError("Google sign up failed. Please try again.");
    } finally {
      setSocialLoading(null);
    }
  }

  async function handleFacebookLogin() {
    setError("");
    setSocialLoading("facebook");
    try {
      const result = await loginWithFacebook();
      if (result.success) {
        await handlePostRegistrationRedirect();
      } else {
        // Handle existing account error specifically
        if (result.error?.includes("Account already exists")) {
          setError(result.error);
        } else {
          setError(
            result.error || "Facebook sign up failed. Please try again.",
          );
        }
      }
    } catch {
      setError("Facebook sign up failed. Please try again.");
    } finally {
      setSocialLoading(null);
    }
  }
  // Phone input should *always* default to India when country is not clearly set.
  const [selectedCountry, setSelectedCountry] = useState<Country>("GB");

  const sanitizeNameInput = (value: string) =>
    value.replace(/[^A-Za-z\s]/g, "");

  const getMaxNationalLength = (country: Country): number => {
    // Hardcoded table is the primary source - fast and always correct
    if (PHONE_MAX_LENGTHS[country] !== undefined) {
      return PHONE_MAX_LENGTHS[country]!;
    }
    // Fallback: compute by iterating lengths and checking isPossible()
    // The nationalNumber.length === len guard ensures lenient parsing doesn't
    // falsely report a shorter number as matching a longer input length.
    try {
      const callingCode = getCountryCallingCode(country);
      let maxFound = 10;
      for (let len = 3; len <= 15; len++) {
        const phone = parsePhoneNumberFromString(
          `+${callingCode}${"9".repeat(len)}`,
          country,
        );
        if (
          phone &&
          phone.isPossible() &&
          phone.nationalNumber.length === len
        ) {
          maxFound = len;
        }
      }
      return maxFound;
    } catch {
      return 10;
    }
  };

  // Handle post-registration redirection to Default sight
  const handlePostRegistrationRedirect = async () => {
    if (!currentShop) {
      window.location.href = "/";
      return;
    }

    try {
      await loadSights(currentShop);

      // If no sights exist, create a default one first
      if (sights.length === 0) {
        const defaultWidgetKeys = [
          "total_sales",
          "total_orders",
          "average_order_value",
          "new_customers",
        ];
        const allWidgets = Object.values(availableWidgets).flat();
        const defaultWidgets = allWidgets
          .filter((widget: any) => defaultWidgetKeys.includes(widget.key))
          .map((widget: any, index: number) => ({
            widget_id: widget.id,
            settings: {},
            position: index,
          }));

        if (defaultWidgets.length > 0) {
          const newSight = await createSight(currentShop, {
            name: "Default",
            description: "Your default dashboard with essential analytics",
            icon: "grid",
            widgets: defaultWidgets,
          });
          const tenantKey = import.meta.env.VITE_REACT_APP_TENANT;
          const tenant = tenantKey ?? encodeURIComponent(currentShop);
          const identifier = newSight.md5_hash ?? newSight.slug;
          window.location.href = `/${tenant}/sight/${identifier}`;
          return;
        }
      }

      // Try to find default sight first
      const defaultSight = sights.find((sight) => sight.slug === "default");
      const tenantKey = import.meta.env.VITE_REACT_APP_TENANT;

      if (defaultSight) {
        const tenant = tenantKey ?? encodeURIComponent(currentShop);
        const identifier = defaultSight.md5_hash ?? defaultSight.slug;
        window.location.href = `/${tenant}/sight/${identifier}`;
        return;
      }

      // If no default sight, show the first available sight
      if (sights.length > 0) {
        const firstSight = sights[0];
        const tenant = tenantKey ?? encodeURIComponent(currentShop);
        const identifier = firstSight.md5_hash ?? firstSight.slug;
        window.location.href = `/${tenant}/sight/${identifier}`;
        return;
      }

      // Fallback to dashboard
      window.location.href = "";
    } catch (error) {
      window.location.href = "";
    }
  };

  // async function handleGoogleLogin() {
  //   setError("");
  //   setSocialLoading("google");

  //   try {
  //     const result = await loginWithGoogle();
  //     if (result.success) {
  //       navigate("");
  //     } else {
  //       setError(result.error || "Google login failed. Please try again.");
  //     }
  //   } catch (err) {
  //     setError("Google login failed. Please try again.");
  //   } finally {
  //     setSocialLoading(null);
  //   }
  // }

  // async function handleFacebookLogin() {
  //   setError("");
  //   setSocialLoading("facebook");

  //   try {
  //     const result = await loginWithFacebook();
  //     if (result.success) {
  //       navigate("");
  //     } else {
  //       setError(result.error || "Facebook login failed. Please try again.");
  //     }
  //   } catch (err) {
  //     setError("Facebook login failed. Please try again.");
  //   } finally {
  //     setSocialLoading(null);
  //   }
  // }

  async function handleAccountSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    // Client-side validation
    if (
      !firstName ||
      !lastName ||
      !email ||
      !phoneNumber?.trim() ||
      !password
    ) {
      setError("Please fill in all required fields.");
      return;
    }

    // if (!isChecked) {
    //   setError("Please accept the terms and conditions.");
    //   return;
    // }

    // if (!websiteUrl.trim()) {
    //   setError("Please enter your website URL.");
    //   return;
    // }

    // try {
    //   const url = new URL(
    //     websiteUrl.startsWith("http") ? websiteUrl : `https://${websiteUrl}`
    //   );

    //   if (!url.hostname.includes(".")) {
    //     throw new Error("Invalid domain");
    //   }

    // } catch (err) {
    //   setError("Please enter a valid website URL (e.g. example.com).");
    //   return;
    // }

    const country = (selectedCountry || "GB") as Country;
    const callingCode = getCountryCallingCode(country);
    const prefix = `+${callingCode}`;
    const nationalDigits = phoneNumber
      ? phoneNumber.startsWith(prefix)
        ? phoneNumber.slice(prefix.length).replace(/\D/g, "")
        : phoneNumber.replace(/^\+\d+/, "").replace(/\D/g, "")
      : "";
    const maxLen = getMaxNationalLength(country);

    if (!phoneNumber || phoneNumber === prefix || nationalDigits.length === 0) {
      setError("Phone number is required.");
      return;
    }
    if (nationalDigits.length < maxLen) {
      setError(
        `Phone number must be ${maxLen - 3} digits for the selected country.`,
      );
      return;
    }
    const parsedPhone = parsePhoneNumberFromString(phoneNumber, country);
    if (!parsedPhone || !parsedPhone.isValid()) {
      setError("Please enter a valid phone number.");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long.");
      return;
    }

    if (passwordConfirmation && password !== passwordConfirmation) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);

    try {
      const name = `${firstName} ${lastName}`.trim();
      const result = await register(
        name,
        email,
        password,
        websiteUrl,
        passwordConfirmation,
        phoneNumber,
      );

      if (result.success) {
        // Register push notifications after successful signup
        const shop = localStorage.getItem("shop") || "";
        if (shop) {
          enablePushNotifications(shop).catch(() => {});
        }

        // After successful registration, always take user directly to dashboard/sight
        // without showing any install modal.
        try {
          await handlePostRegistrationRedirect();
        } finally {
          setLoading(false);
        }
      } else {
        setError(result.error || "Registration failed. Please try again.");
        setLoading(false);
      }
    } catch (err) {
      setError("Registration failed. Please try again.");
      setLoading(false);
    }
  }

  const handlePhoneChange = (value?: string) => {
    const country = (selectedCountry || "GB") as Country;
    const callingCode = getCountryCallingCode(country);
    const prefix = `+${callingCode}`;

    // Never allow clearing - restore country code
    if (!value || !value.startsWith("+")) {
      setPhoneNumber(prefix);
      setPhoneError(null);
      return;
    }

    const maxLen = getMaxNationalLength(country);

    // Count only the national digits from the raw value (strip country code + non-digits)
    const nationalDigits = value.startsWith(prefix)
      ? value.slice(prefix.length).replace(/\D/g, "")
      : value.replace(/^\+\d+/, "").replace(/\D/g, "");

    // Hard block: reject any input that exceeds the max length for this country
    if (nationalDigits.length > maxLen) {
      return;
    }

    // Update country selection if libphonenumber detects a different country
    const phone = parsePhoneNumberFromString(value, country);
    if (phone?.country) {
      setSelectedCountry(phone.country as Country);
    }

    // Show inline error only when all digits are entered and number is invalid
    if (nationalDigits.length === maxLen) {
      if (phone && phone.isValid()) {
        setPhoneError(null);
      } else {
        setPhoneError("Invalid phone number for the selected country.");
      }
    } else {
      setPhoneError(null);
    }

    setPhoneNumber(value);
  };

  const handlePhoneBlur = () => {
    const country = (selectedCountry || "GB") as Country;
    const callingCode = getCountryCallingCode(country);
    const prefix = `+${callingCode}`;

    if (!phoneNumber || phoneNumber === prefix) {
      setPhoneError("Phone number is required.");
      return;
    }

    const maxLen = getMaxNationalLength(country);
    const nationalDigits = phoneNumber.startsWith(prefix)
      ? phoneNumber.slice(prefix.length).replace(/\D/g, "")
      : phoneNumber.replace(/^\+\d+/, "").replace(/\D/g, "");

    if (nationalDigits.length < maxLen) {
      setPhoneError(
        `Phone number must be ${maxLen} digits for the selected country.`,
      );
      return;
    }

    const phone = parsePhoneNumberFromString(phoneNumber, country);
    if (!phone || !phone.isValid()) {
      setPhoneError("Invalid phone number for the selected country.");
    } else {
      setPhoneError(null);
    }
  };

  return (
    <div className="relative dark:bg-[#080f1c]">
      <img
        src="/images/shape/grid-01.svg"
        alt=""
        aria-hidden="true"
        loading="lazy"
        fetchpriority="low"
        className="pointer-events-none fixed right-0 top-0 w-[450px] xl:w-[750px] opacity-80 dark:opacity-90 dark:[filter:brightness(0)_invert(1)]"
        style={{ zIndex: 0 }}
      />
      <img
        src="/images/shape/grid-01.svg"
        alt=""
        aria-hidden="true"
        loading="lazy"
        fetchpriority="low"
        className="pointer-events-none fixed left-0 bottom-0 w-[450px] xl:w-[750px] rotate-180 opacity-80 dark:opacity-90 dark:[filter:brightness(0)_invert(1)]"
      />
      <div className="flex justify-center sm:mt-10 sm:mb-10 mt-2 mb-2">
        <Link to="/" className="dark:hidden">
          <img
            className="w-[120px] h-auto sm:w-[200px] sm:h-[42px]"
            src="/images/logo/netsight-Black.svg"
            alt="Logo"
          />
        </Link>
        <Link to="/" className="hidden dark:block">
          <img
            className="w-[120px] h-auto sm:w-[200px] sm:h-[42px]"
            src="/images/logo/netsight-white.svg"
            alt="Logo"
          />
        </Link>
      </div>
      <div
        className="relative flex flex-col justify-center flex-1 w-full max-w-md mx-auto dark:bg-[#0d1828] rounded-md border border-gray-100 dark:border-[#FFFFFF0F] shadow-theme-lg"
        style={{ zIndex: 3 }}
      >
        <div className="free-badge rounded-md border border-dashed border-gray-400 bg-white sm:p-1 dark:border-white/20 dark:bg-[#111f33] relative overflow-hidden text-center -mb-5">
          <span className="free-label absolute font-bold text-white/90 text-center">
            FREE
          </span>
          <h2 className="font-bold text-gray-800 dark:text-white/90 sm:text-xl text-base text-center">
            <p className="text-gray-400 ">
              <del className="sm:text-xl text-base">$ 19/month</del>
            </p>
            Create your account
          </h2>
          <p className="sm:text-sm text-xs text-gray-500 dark:text-gray-400 mb-2">
            Start your 30-day free trial. No credit card required.
          </p>
        </div>
        <div className="sm:p-6 px-6 pt-6 pb-3 ">
          <div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-1 sm:gap-5 sm:mt-3 mt-1">
              <button
                onClick={handleGoogleLogin}
                disabled={socialLoading === "google"}
                className="inline-flex items-center justify-center gap-2 sm:py-2 py-1 text-sm font-normal text-gray-700 transition-colors bg-gray-100 dark:bg-[#111f33] rounded-[6px] px-1 hover:bg-gray-200 hover:text-gray-800 dark:bg-white/5 dark:text-white/90 dark:hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18.7511 10.1944C18.7511 9.47495 18.6915 8.94995 18.5626 8.40552H10.1797V11.6527H15.1003C15.0011 12.4597 14.4654 13.675 13.2749 14.4916L13.2582 14.6003L15.9087 16.6126L16.0924 16.6305C17.7788 15.1041 18.7511 12.8583 18.7511 10.1944Z"
                    fill="#4285F4"
                  />
                  <path
                    d="M10.1788 18.75C12.5895 18.75 14.6133 17.9722 16.0915 16.6305L13.274 14.4916C12.5201 15.0068 11.5081 15.3666 10.1788 15.3666C7.81773 15.3666 5.81379 13.8402 5.09944 11.7305L4.99473 11.7392L2.23868 13.8295L2.20264 13.9277C3.67087 16.786 6.68674 18.75 10.1788 18.75Z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.10014 11.7305C4.91165 11.186 4.80257 10.6027 4.80257 9.99992C4.80257 9.3971 4.91165 8.81379 5.09022 8.26935L5.08523 8.1534L2.29464 6.02954L2.20333 6.0721C1.5982 7.25823 1.25098 8.5902 1.25098 9.99992C1.25098 11.4096 1.5982 12.7415 2.20333 13.9277L5.10014 11.7305Z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M10.1789 4.63331C11.8554 4.63331 12.9864 5.34303 13.6312 5.93612L16.1511 3.525C14.6035 2.11528 12.5895 1.25 10.1789 1.25C6.68676 1.25 3.67088 3.21387 2.20264 6.07218L5.08953 8.26943C5.81381 6.15972 7.81776 4.63331 10.1789 4.63331Z"
                    fill="#EB4335"
                  />
                </svg>
                {socialLoading === "google"
                  ? "Signing in..."
                  : "Sign up with Google"}
              </button>
              {/* <button
                onClick={handleFacebookLogin}
                disabled={socialLoading === "facebook"}
                className="inline-flex items-center justify-center gap-3 py-3 text-sm font-normal text-gray-700 transition-colors bg-gray-100 rounded-lg px-3 hover:bg-gray-200 hover:text-gray-800 dark:bg-white/5 dark:text-white/90 dark:hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed">
                <div className=" connection-icon-bg">
                    <MetaIcon className="w-6 h-6" />
                </div>
                {socialLoading === "facebook" ? "Signing in..." : "Sign up with Facebook"}
              </button> */}
            </div>
            <div className="relative  sm:py-2 flex items-center">
              {/* Left line */}
              <div className="flex-grow border-t border-gray-200 dark:border-gray-800"></div>

              {/* Text */}
              <span className="mx-1 text-sm text-gray-400">Or</span>

              {/* Right line */}
              <div className="flex-grow border-t border-gray-200 dark:border-gray-800"></div>
            </div>
            <form onSubmit={handleAccountSubmit}>
              <div className="space-y-4 sm:-mt-2 mt-0">
                {error && (
                  <div className="sm:p-3 p-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg dark:bg-red-900/20 dark:text-red-400 dark:border-red-800">
                    {error}
                  </div>
                )}
                <div className="grid gap-2 sm:gap-5 grid-cols-2 ">
                  {/* <!-- First Name --> */}
                  <div className="sm:col-span-1 sm:mt-0 -mt-1">
                    <Label className="dark:text-white">
                      First Name<span className="text-error-500"> *</span>
                    </Label>
                    <Input
                      type="text"
                      id="fname"
                      name="fname"
                      placeholder="Enter your first name"
                      value={firstName}
                      onChange={(e) =>
                        setFirstName(sanitizeNameInput(e.target.value))
                      }
                    />
                  </div>
                  {/* <!-- Last Name --> */}
                  {/* <div className="sm:col-span-1 mt-[-1rem] sm:mt-0"> */}
                  <div className="sm:col-span-1 sm:mt-0 -mt-1">
                    <Label className="dark:text-white">
                      Last Name<span className="text-error-500"> *</span>
                    </Label>
                    <Input
                      type="text"
                      id="lname"
                      name="lname"
                      placeholder="Enter your last name"
                      value={lastName}
                      onChange={(e) =>
                        setLastName(sanitizeNameInput(e.target.value))
                      }
                    />
                  </div>
                </div>
                {/* <!-- Email --> */}
                <div>
                  <Label className="dark:text-white mb-1 mt-2">
                    Email<span className="text-error-500"> *</span>
                  </Label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                {/* <!-- Phone Number --> */}
                <div className="">
                  <Label className="dark:text-white mt-2 ">
                    Phone Number<span className="text-error-500"> *</span>
                  </Label>

                  {geoLoading ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                      Detecting country…
                    </p>
                  ) : geoError ? (
                    <p className="text-sm text-amber-600 dark:text-amber-400 mb-2">
                      Country could not be detected. You can still select a
                      country in the field below.
                    </p>
                  ) : null}

                  <PhoneInput
                    countryCallingCodeEditable={false}
                    international
                    countrySelectComponent={PhoneCountrySelectDrawer}
                    id="phone"
                    name="phone"
                    placeholder="Enter your phone number"
                    value={
                      phoneNumber ||
                      `+${getCountryCallingCode(selectedCountry || "GB")}`
                    }
                    onChange={handlePhoneChange}
                    onBlur={handlePhoneBlur}
                    defaultCountry={selectedCountry}
                    onCountryChange={(c) => {
                      const nextCountry = (c || "GB") as Country;
                      setSelectedCountry(nextCountry);
                      const callingCode = getCountryCallingCode(nextCountry);
                      setPhoneNumber(`+${callingCode}`);
                      setPhoneError(null);
                    }}
                    className="phone-input-container h-11 border border-gray-300 dark:border-gray-700 rounded-lg px-0.5 -mb-5 sm:mb-0 -mt-0.5 sm:mt-0 bg-white dark:bg-gray-900 hover:border-brand-500 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/20 transition-colors [&_.PhoneInputCountry]:mr-0 [&_.PhoneInputCountry]:pl-0 [&_.PhoneInputCountry]:pr-0 [&_.PhoneInputInput]:pl-0"
                    numberInputProps={{
                      className:
                        "h-11 w-full bg-transparent rounded-r-lg text-sm mx-auto border-0 ring-0 shadow-none outline-none focus:outline-none focus:ring-0 focus:shadow-none focus-visible:outline-none focus-visible:ring-0 dark:text-white/90",
                      onKeyDown: (
                        e: React.KeyboardEvent<HTMLInputElement>,
                      ) => {
                        if (!/^\d$/.test(e.key)) return;
                        const country = (selectedCountry || "GB") as Country;
                        const cc = getCountryCallingCode(country);
                        const maxLen = getMaxNationalLength(country);
                        const totalDigits = e.currentTarget.value.replace(
                          /\D/g,
                          "",
                        );
                        const nationalDigits = totalDigits.slice(cc.length);
                        if (nationalDigits.length >= maxLen) {
                          e.preventDefault();
                        }
                      },
                    }}
                    countrySelectProps={{
                      className:
                        "h-11 rounded-l-lg bg-transparent px-0 border-0 ring-0 shadow-none outline-none focus:outline-none focus:ring-0 focus:shadow-none focus-visible:outline-none focus-visible:ring-0 dark:text-white/90",
                    }}
                  />
                  {phoneError && (
                    <p className="text-sm text-red-600 sm:mt-1 mt-5 -mb-6 sm:-mb-0">{phoneError}</p>
                  )}
                </div>
                {/* <!-- Shop --> */}
                {/* <div>
                  <Label className="dark:text-white">
                    Website URL<span className="text-error-500"> *</span>
                  </Label>
                  <Input
                    type="text"
                    id="website"
                    name="website"
                    placeholder="https://www.example.com"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                  />
                </div> */}
                <div className="grid grid-cols-1 gap-5 mt-10 sm:grid-cols-2 sm:mt-5">
                  {/* <!-- Password --> */}
                  <div>
                    <Label className="dark:text-white  ">
                      Password<span className="text-error-500 "> *</span>
                    </Label>
                    <div className="relative">
                      <Input
                        placeholder="Enter your password"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                      <span
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute z-30 -translate-y-1/2 cursor-pointer right-4 top-1/2"
                      >
                        {showPassword ? (
                          <EyeIcon className="fill-gray-500 dark:fill-gray-400 size-5" />
                        ) : (
                          <EyeCloseIcon className="fill-gray-500 dark:fill-gray-400 size-5" />
                        )}
                      </span>
                    </div>
                  </div>
                  {/* <!-- Password Confirmation --> */}
                  <div >
                    <Label className="dark:text-white ">
                      Confirm Password<span className="text-error-500"> *</span>
                    </Label>
                    <div className="relative">
                      <Input
                        placeholder="Confirm your password"
                        type={showPassword ? "text" : "password"}
                        value={passwordConfirmation}
                        onChange={(e) =>
                          setPasswordConfirmation(e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>
                {/* <!-- Checkbox --> */}
                <div className="flex items-center gap-3 justify-center">
                  {/* <Checkbox
                    className="w-5 h-5"
                    checked={isChecked}
                    onChange={setIsChecked}
                  /> */}
                  <p className="inline-block font-normal text-gray-700 dark:text-white text-sm  text-center">
                    By signing up you agree to the{" "}
                    <a
                      href="https://netsights.ai/terms-conditions/"
                      className="text-brand-500 hover:text-brand-600 dark:text-brand-400"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms
                    </a>{" "}
                    and{" "}
                    <a
                      href="https://netsights.ai/privacy-policy/"
                      className="text-brand-500 hover:text-brand-600 dark:text-brand-400"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Privacy Policy
                    </a>
                  </p>
                </div>
                {/* <!-- Button --> */}
                <div>
                  <button
                    disabled={loading}
                    className="flex items-center justify-center w-full px-6 py-2 text-sm font-medium text-white transition rounded-[6px] bg-teal-600 hover:bg-teal-700 disabled:opacity-60 "
                  >
                    {loading ? "Saving..." : "Continue"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
        {/* <div className="block lg:hidden mb-4 flex flex-col items-center mt-5">
          <img className="my-3 md:max-w-sm mx-auto"
            src="/images/logo/signup-page-mobile.png"
            alt="Netsight"
          />
          <ul className="text-gray-600 dark:text-white width-full dot-primary-si text-center">
            <li><span></span> Integrate within 2 mins</li>
            <li><span></span> Get daily reports on WhatsApp</li>
            <li><span></span> Get key D2C metrics at one place</li>
          </ul>
        </div> */}
      </div>
      <div className="sm:mt-8 sm:mb-8 mt-2 mb-2">
        <p className="text-sm font-normal text-center text-gray-700 dark:text-white sm:text-center">
          Already have an account?{" "}
          <Link
            to="/signin"
            className="text-brand-500 hover:text-brand-600 dark:text-brand-400"
          >
            {" "}
            Sign in
          </Link>
        </p>
      </div>
      {/* Install App Modal */}
      {showInstallModal && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-md p-6 bg-white rounded-lg shadow-lg dark:bg-gray-900">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">
              Install App on Shopify
            </h2>
            <p className="mt-3 text-sm text-gray-600 dark:text-white">
              Install the Netsight app on{" "}
              <span className="font-medium text-gray-900 dark:text-white">
                {pendingShop.replace(".myshopify.com", "")}
              </span>{" "}
              to finish setup.
            </p>
            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                onClick={async () => {
                  setShowInstallModal(false);
                  // Redirect to embedded app or dashboard based on context
                  const host = searchParams.get("host");
                  if (host && pendingShop) {
                    // In embedded context, redirect to embedded app
                    const embeddedUrl = `/shopify-app?${new URLSearchParams({
                      shop: pendingShop,
                      host: host,
                      billing: "required",
                    }).toString()}`;
                    window.location.href = embeddedUrl;
                  } else {
                    // Not in embedded context, redirect to dashboard
                    await handlePostRegistrationRedirect();
                  }
                }}
                className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-950 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (installUrl) {
                    const host = searchParams.get("host");
                    // If in embedded context, redirect to embedded app route
                    if (host && pendingShop) {
                      const embeddedUrl = `/shopify-app?${new URLSearchParams({
                        shop: pendingShop,
                        host: host,
                        billing: "required",
                      }).toString()}`;
                      window.location.href = embeddedUrl;
                    } else {
                      // Not in embedded context, use install URL directly
                      window.location.href = installUrl;
                    }
                  }
                }}
                disabled={!installUrl}
                className="px-4 py-2 text-sm font-medium text-white transition-colors bg-teal-600 hover:bg-teal-700 rounded-[6px] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Install App
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
